import { NO_RESPONSE } from "../api";

export type SessionAction = "retry" | "login" | "denied" | "render";

/**
 * What to do about a /api/me answer. Framework-free and pure so it can be
 * tested without a DOM or a React tree.
 *
 * Two distinctions carry this function, and both are failure modes the spec
 * names rather than theory.
 *
 * NO_RESPONSE is not a rejection: sending someone to the Hub because a deploy
 * was mid-flight logs everybody out during a deploy (§3.3).
 *
 * 403 is not 401. A denied user is AUTHENTICATED — they simply hold no grant
 * for this app. Signing them in again mints the same token with the same
 * missing claim, so treating 403 as "login" is an infinite redirect: app →
 * Slack → app → Slack. request() has already sent them to the Hub's modal,
 * which names the app and tells them who to ask; nothing here may overwrite
 * that with a second redirect. §0.5's third proof is exactly "the modal, not a
 * redirect loop" — and this was reported from production once already.
 */
export function nextAction(status: number, hasUser: boolean): SessionAction {
  if (status === NO_RESPONSE) return "retry";
  if (status === 403) return "denied";
  if (status === 401 || !hasUser) return "login";
  return "render";
}

/** Roles for this app, highest first. Hierarchy is admin > user > viewer (§3.4). */
export const ROLES = ["admin", "user", "viewer"] as const;
export type AppRole = (typeof ROLES)[number];

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  avatarUrl: string | null;
  /** The role for THIS app, from `apps[APP_SLUG]` — never the Hub-wide `role`. */
  appRole: string;
  /** The team member this person is (by work email), or null if unlinked. */
  memberId: string | null;
}

/** True when `role` is at least `required` in the admin > user > viewer order. */
export function hasAtLeast(role: string | undefined, required: AppRole): boolean {
  const have = ROLES.indexOf(role as AppRole);
  const need = ROLES.indexOf(required);
  return have !== -1 && have <= need;
}
